import { authHeaders, getBackendUrl } from "../lib/http";
import { useActiveShopId } from "../store/shop.store";
import { useState, useMemo, useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Package,
  Loader2,
  RefreshCw,
  AlertCircle,
  AlertTriangle,
  Filter,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Check,
  X,
  Edit3,
  ChevronDown,
  ChevronUp,
  Boxes,
  PackageX,
  Archive,
  Image,
} from "lucide-react";
import { clsx } from "clsx";

interface ListingImage {
  listing_image_id: number;
  url_170x135: string;
  url_570xN: string;
}

interface EtsyListing {
  listing_id: number;
  title: string;
  state: string;
  price: { amount: number; divisor: number; currency_code: string };
  quantity: number;
  views: number;
  images?: ListingImage[];
}

interface InventoryProduct {
  product_id: number;
  sku: string;
  offerings: Array<{
    offering_id: number;
    price: { amount: number; divisor: number; currency_code: string };
    quantity: number;
    is_enabled: boolean;
  }>;
  property_values: Array<{
    property_name: string;
    values: string[];
  }>;
}

interface InventoryData {
  products: InventoryProduct[];
  listing_id: number;
}

type StockFilter = "all" | "low" | "out";
type SortField = "quantity" | "price" | "title";
type SortDir = "asc" | "desc";

async function fetchListings(): Promise<EtsyListing[]> {
  const params = new URLSearchParams({
    state: "active",
    includes: "images",
    limit: "100",
  });
  const res = await fetch(`${getBackendUrl()}/api/etsy/listings?${params}`, {
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error("Failed to load listings");
  const json = await res.json();
  const data = json.data ?? json;
  return data.results ?? data;
}

async function fetchInventory(listingId: number): Promise<InventoryData> {
  const res = await fetch(
    `${getBackendUrl()}/api/etsy/listings/${listingId}/inventory`,
    { headers: authHeaders() }
  );
  if (!res.ok) throw new Error("Failed to load inventory");
  const json = await res.json();
  return json.data ?? json;
}

async function updateInventory(
  listingId: number,
  products: InventoryProduct[]
): Promise<void> {
  const res = await fetch(
    `${getBackendUrl()}/api/etsy/listings/${listingId}/inventory`,
    {
      method: "PATCH",
      headers: authHeaders(),
      body: JSON.stringify({ products }),
    }
  );
  if (!res.ok) throw new Error("Failed to update inventory");
}

function formatPrice(price: { amount: number; divisor: number; currency_code: string }) {
  return `${(price.amount / price.divisor).toFixed(2)} ${price.currency_code}`;
}

function InventoryRow({
  listing,
  isSelected,
  onToggleSelect,
}: {
  listing: EtsyListing;
  isSelected: boolean;
  onToggleSelect: () => void;
}) {
  const queryClient = useQueryClient();
  const [expanded, setExpanded] = useState(false);
  const [editingQty, setEditingQty] = useState(false);
  const [newQty, setNewQty] = useState(String(listing.quantity));
  const shopId = useActiveShopId();

  const {
    data: inventory,
    isLoading: invLoading,
    isError: invError,
  } = useQuery({
    queryKey: ["inventory", shopId, listing.listing_id],
    queryFn: () => fetchInventory(listing.listing_id),
    enabled: expanded,
  });

  const updateM = useMutation({
    mutationFn: (products: InventoryProduct[]) =>
      updateInventory(listing.listing_id, products),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["inventory", listing.listing_id] });
      queryClient.invalidateQueries({ queryKey: ["inventory-listings"] });
      setEditingQty(false);
    },
  });

  const handleSaveQty = useCallback(() => {
    const qty = parseInt(newQty, 10);
    if (isNaN(qty) || qty < 0) return;

    if (inventory?.products?.length) {
      const updated = inventory.products.map((p) => ({
        ...p,
        offerings: p.offerings.map((o) => ({ ...o, quantity: qty })),
      }));
      updateM.mutate(updated);
    } else {
      // Fallback: just update the listing quantity via inventory endpoint
      updateM.mutate([]);
    }
  }, [newQty, inventory, updateM]);

  const isLow = listing.quantity > 0 && listing.quantity <= 5;
  const isOut = listing.quantity === 0;
  const thumbnail = listing.images?.[0]?.url_170x135;

  return (
    <div
      className={clsx(
        "rounded-xl border bg-white transition-shadow",
        isOut
          ? "border-red-200 bg-red-50/30"
          : isLow
          ? "border-orange-200 bg-orange-50/30"
          : "border-gray-200"
      )}
    >
      <div className="flex items-center gap-3 p-4">
        {/* Checkbox */}
        <input
          type="checkbox"
          checked={isSelected}
          onChange={onToggleSelect}
          className="h-4 w-4 rounded border-gray-300 text-orange-500 focus:ring-orange-400"
        />

        {/* Thumbnail */}
        <div className="h-12 w-12 flex-shrink-0 rounded-lg bg-gray-100 overflow-hidden">
          {thumbnail ? (
            <img src={thumbnail} alt="" className="h-full w-full object-cover" />
          ) : (
            <div className="flex h-full w-full items-center justify-center">
              <Image className="h-5 w-5 text-gray-300" />
            </div>
          )}
        </div>

        {/* Title */}
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-medium text-gray-900 truncate">{listing.title}</h3>
          <div className="mt-0.5 flex items-center gap-3 text-xs text-gray-500">
            <span>{formatPrice(listing.price)}</span>
            <span>{listing.views} views</span>
          </div>
        </div>

        {/* Quantity (editable) */}
        <div className="flex items-center gap-2 flex-shrink-0">
          {editingQty ? (
            <div className="flex items-center gap-1">
              <input
                type="number"
                min={0}
                value={newQty}
                onChange={(e) => setNewQty(e.target.value)}
                className="w-16 rounded border border-gray-300 px-2 py-1 text-sm text-center focus:border-orange-400 focus:ring-1 focus:ring-orange-400"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleSaveQty();
                  if (e.key === "Escape") {
                    setEditingQty(false);
                    setNewQty(String(listing.quantity));
                  }
                }}
              />
              <button
                onClick={handleSaveQty}
                disabled={updateM.isPending}
                className="rounded p-1 text-emerald-600 hover:bg-emerald-50"
              >
                {updateM.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Check className="h-4 w-4" />
                )}
              </button>
              <button
                onClick={() => {
                  setEditingQty(false);
                  setNewQty(String(listing.quantity));
                }}
                className="rounded p-1 text-gray-400 hover:bg-gray-100"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          ) : (
            <button
              onClick={() => {
                setNewQty(String(listing.quantity));
                setEditingQty(true);
              }}
              className={clsx(
                "flex items-center gap-1 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors",
                isOut
                  ? "bg-red-100 text-red-700 hover:bg-red-200"
                  : isLow
                  ? "bg-orange-100 text-orange-700 hover:bg-orange-200"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              )}
              title="Click to edit quantity"
            >
              <Edit3 className="h-3 w-3" />
              {listing.quantity}
            </button>
          )}
        </div>

        {/* Low stock badge */}
        {isOut && (
          <span className="flex-shrink-0 rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700">
            Out of stock
          </span>
        )}
        {isLow && !isOut && (
          <span className="flex-shrink-0 rounded-full bg-orange-100 px-2 py-0.5 text-xs font-medium text-orange-700">
            Low stock
          </span>
        )}

        {/* Expand */}
        <button
          onClick={() => setExpanded(!expanded)}
          className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100"
          title="Show inventory details"
        >
          {expanded ? (
            <ChevronUp className="h-4 w-4" />
          ) : (
            <ChevronDown className="h-4 w-4" />
          )}
        </button>
      </div>

      {/* Expanded inventory details */}
      {expanded && (
        <div className="border-t border-gray-100 px-4 py-3 bg-gray-50/50">
          {invLoading && (
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading inventory...
            </div>
          )}
          {invError && (
            <div className="flex items-center gap-2 text-sm text-red-600">
              <AlertCircle className="h-4 w-4" />
              Failed to load inventory details
            </div>
          )}
          {inventory && inventory.products?.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                Variants ({inventory.products.length})
              </p>
              {inventory.products.map((product) => (
                <div
                  key={product.product_id}
                  className="flex items-center justify-between rounded-lg bg-white border border-gray-100 px-3 py-2 text-sm"
                >
                  <div className="flex items-center gap-2">
                    {product.sku && (
                      <span className="text-xs text-gray-400">SKU: {product.sku}</span>
                    )}
                    {product.property_values?.map((pv) => (
                      <span
                        key={pv.property_name}
                        className="rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-600"
                      >
                        {pv.property_name}: {pv.values.join(", ")}
                      </span>
                    ))}
                  </div>
                  <div className="flex items-center gap-4 text-xs text-gray-600">
                    {product.offerings?.map((off) => (
                      <span key={off.offering_id}>
                        {formatPrice(off.price)} / qty: {off.quantity}
                        {!off.is_enabled && (
                          <span className="ml-1 text-red-500">(disabled)</span>
                        )}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
          {inventory && (!inventory.products || inventory.products.length === 0) && (
            <p className="text-sm text-gray-500">No variant data available</p>
          )}
        </div>
      )}
    </div>
  );
}

export default function InventoryManagementPage() {
  const queryClient = useQueryClient();
  const [stockFilter, setStockFilter] = useState<StockFilter>("all");
  const [sortField, setSortField] = useState<SortField>("quantity");
  const [sortDir, setSortDir] = useState<SortDir>("asc");
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [bulkQty, setBulkQty] = useState("");
  const [showBulkForm, setShowBulkForm] = useState(false);

  const shopId = useActiveShopId();
  const { data: listings, isLoading, isError, error } = useQuery({
    queryKey: ["inventory-listings", shopId],
    queryFn: fetchListings,
  });

  const bulkUpdateM = useMutation({
    mutationFn: async (params: { ids: number[]; quantity: number }) => {
      const patchPromises = params.ids.map((id) =>
        fetch(`${getBackendUrl()}/api/etsy/listings/${id}/inventory`, {
          method: "PATCH",
          headers: authHeaders(),
          body: JSON.stringify({ quantity: params.quantity }),
        })
      );
      await Promise.all(patchPromises);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["inventory-listings"] });
      setSelectedIds(new Set());
      setShowBulkForm(false);
      setBulkQty("");
    },
  });

  const allListings = listings || [];

  // Computed stats
  const totalSKUs = allListings.length;
  const totalInventory = allListings.reduce((s, l) => s + l.quantity, 0);
  const lowStockCount = allListings.filter(
    (l) => l.quantity > 0 && l.quantity <= 5
  ).length;
  const outOfStockCount = allListings.filter((l) => l.quantity === 0).length;

  // Filter
  const filtered = useMemo(() => {
    let items = [...allListings];
    if (stockFilter === "low") items = items.filter((l) => l.quantity > 0 && l.quantity <= 5);
    if (stockFilter === "out") items = items.filter((l) => l.quantity === 0);
    return items;
  }, [allListings, stockFilter]);

  // Sort
  const sorted = useMemo(() => {
    const items = [...filtered];
    items.sort((a, b) => {
      let cmp = 0;
      if (sortField === "quantity") cmp = a.quantity - b.quantity;
      else if (sortField === "price")
        cmp = a.price.amount / a.price.divisor - b.price.amount / b.price.divisor;
      else if (sortField === "title") cmp = a.title.localeCompare(b.title);
      return sortDir === "asc" ? cmp : -cmp;
    });
    return items;
  }, [filtered, sortField, sortDir]);

  function toggleSort(field: SortField) {
    if (sortField === field) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDir("asc");
    }
  }

  function toggleSelect(id: number) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleSelectAll() {
    if (selectedIds.size === sorted.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(sorted.map((l) => l.listing_id)));
    }
  }

  function SortIcon({ field }: { field: SortField }) {
    if (sortField !== field) return <ArrowUpDown className="h-3.5 w-3.5 text-gray-400" />;
    return sortDir === "asc" ? (
      <ArrowUp className="h-3.5 w-3.5 text-orange-500" />
    ) : (
      <ArrowDown className="h-3.5 w-3.5 text-orange-500" />
    );
  }

  return (
    <div className="mx-auto max-w-6xl p-6">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Boxes className="h-6 w-6 text-orange-500" />
            Inventory Management
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            Monitor and manage your stock levels
          </p>
        </div>
        <button
          onClick={() =>
            queryClient.invalidateQueries({ queryKey: ["inventory-listings"] })
          }
          className="flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50"
        >
          <RefreshCw className="h-4 w-4" />
          Refresh
        </button>
      </div>

      {/* Stats Bar */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        <div className="rounded-xl border border-gray-200 bg-white p-4 text-center">
          <p className="text-xs text-gray-500 mb-1">Total SKUs</p>
          <p className="text-xl font-bold text-gray-900">{totalSKUs}</p>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-4 text-center">
          <p className="text-xs text-gray-500 mb-1">Total Inventory</p>
          <p className="text-xl font-bold text-gray-900">{totalInventory.toLocaleString()}</p>
        </div>
        <div className="rounded-xl border border-orange-200 bg-orange-50 p-4 text-center">
          <p className="text-xs text-orange-600 mb-1">Low Stock</p>
          <p className="text-xl font-bold text-orange-700">{lowStockCount}</p>
        </div>
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-center">
          <p className="text-xs text-red-600 mb-1">Out of Stock</p>
          <p className="text-xl font-bold text-red-700">{outOfStockCount}</p>
        </div>
      </div>

      {/* Error */}
      {isError && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-4 flex items-center gap-2">
          <AlertCircle className="h-5 w-5 text-red-500" />
          <p className="text-sm text-red-700">{(error as Error).message}</p>
        </div>
      )}

      {/* Filters & Sort */}
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-gray-400" />
          {(
            [
              { value: "all", label: "All", icon: Package },
              { value: "low", label: "Low Stock", icon: AlertTriangle },
              { value: "out", label: "Out of Stock", icon: PackageX },
            ] as const
          ).map((opt) => (
            <button
              key={opt.value}
              onClick={() => setStockFilter(opt.value)}
              className={clsx(
                "flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors",
                stockFilter === opt.value
                  ? "bg-orange-100 text-orange-700 border border-orange-200"
                  : "text-gray-500 hover:bg-gray-100"
              )}
            >
              <opt.icon className="h-3.5 w-3.5" />
              {opt.label}
            </button>
          ))}
        </div>

        <div className="h-5 w-px bg-gray-200" />

        <div className="flex items-center gap-1 text-sm text-gray-500">
          Sort:
          {(
            [
              { field: "quantity" as SortField, label: "Quantity" },
              { field: "price" as SortField, label: "Price" },
              { field: "title" as SortField, label: "Title" },
            ] as const
          ).map((opt) => (
            <button
              key={opt.field}
              onClick={() => toggleSort(opt.field)}
              className={clsx(
                "flex items-center gap-1 rounded-lg px-2 py-1 text-sm transition-colors",
                sortField === opt.field
                  ? "bg-gray-100 text-gray-900 font-medium"
                  : "text-gray-500 hover:bg-gray-50"
              )}
            >
              {opt.label}
              <SortIcon field={opt.field} />
            </button>
          ))}
        </div>
      </div>

      {/* Bulk Actions */}
      {selectedIds.size > 0 && (
        <div className="mb-4 flex items-center gap-3 rounded-lg border border-orange-200 bg-orange-50 p-3">
          <span className="text-sm font-medium text-orange-700">
            {selectedIds.size} selected
          </span>
          {showBulkForm ? (
            <div className="flex items-center gap-2">
              <input
                type="number"
                min={0}
                placeholder="New quantity"
                value={bulkQty}
                onChange={(e) => setBulkQty(e.target.value)}
                className="w-28 rounded border border-gray-300 px-2 py-1 text-sm focus:border-orange-400 focus:ring-1 focus:ring-orange-400"
              />
              <button
                onClick={() => {
                  const qty = parseInt(bulkQty, 10);
                  if (!isNaN(qty) && qty >= 0) {
                    bulkUpdateM.mutate({ ids: Array.from(selectedIds), quantity: qty });
                  }
                }}
                disabled={bulkUpdateM.isPending || !bulkQty}
                className="flex items-center gap-1 rounded-lg bg-orange-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-orange-600 disabled:opacity-40"
              >
                {bulkUpdateM.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                Apply
              </button>
              <button
                onClick={() => {
                  setShowBulkForm(false);
                  setBulkQty("");
                }}
                className="text-sm text-gray-500 hover:text-gray-700"
              >
                Cancel
              </button>
            </div>
          ) : (
            <button
              onClick={() => setShowBulkForm(true)}
              className="rounded-lg bg-orange-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-orange-600"
            >
              Set Quantity
            </button>
          )}
          <button
            onClick={() => setSelectedIds(new Set())}
            className="ml-auto text-sm text-gray-500 hover:text-gray-700"
          >
            Clear selection
          </button>
        </div>
      )}

      {/* Select All */}
      {sorted.length > 0 && (
        <div className="mb-2 flex items-center gap-2 px-4">
          <input
            type="checkbox"
            checked={selectedIds.size === sorted.length && sorted.length > 0}
            onChange={toggleSelectAll}
            className="h-4 w-4 rounded border-gray-300 text-orange-500 focus:ring-orange-400"
          />
          <span className="text-xs text-gray-500">Select all ({sorted.length})</span>
        </div>
      )}

      {/* Loading */}
      {isLoading && (
        <div className="space-y-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <div
              key={i}
              className="animate-pulse rounded-xl border border-gray-200 bg-white p-4 flex items-center gap-3"
            >
              <div className="h-4 w-4 rounded bg-gray-200" />
              <div className="h-12 w-12 rounded-lg bg-gray-200" />
              <div className="flex-1 space-y-2">
                <div className="h-4 w-48 rounded bg-gray-200" />
                <div className="h-3 w-32 rounded bg-gray-100" />
              </div>
              <div className="h-8 w-16 rounded bg-gray-200" />
            </div>
          ))}
        </div>
      )}

      {/* Empty */}
      {!isLoading && sorted.length === 0 && (
        <div className="text-center py-16">
          {stockFilter !== "all" ? (
            <>
              <Archive className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">
                No listings match the current filter
              </p>
              <button
                onClick={() => setStockFilter("all")}
                className="mt-2 text-sm text-orange-600 hover:underline"
              >
                Show all listings
              </button>
            </>
          ) : (
            <>
              <Package className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <h2 className="text-lg font-semibold text-gray-700 mb-1">
                No inventory data
              </h2>
              <p className="text-sm text-gray-500">
                Connect your Etsy shop to manage inventory here.
              </p>
            </>
          )}
        </div>
      )}

      {/* Listing Grid */}
      {!isLoading && sorted.length > 0 && (
        <div className="space-y-3">
          {sorted.map((listing) => (
            <InventoryRow
              key={listing.listing_id}
              listing={listing}
              isSelected={selectedIds.has(listing.listing_id)}
              onToggleSelect={() => toggleSelect(listing.listing_id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
